import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Form, FormInput, FormButton, Button, Dropdown } from '../../../Common';
import { useFormContext, Controller } from 'react-hook-form';
import { Vendor } from '../../../../types';
import '../DiscountForm.common.css';

export interface DiscountFormData {
  code: string;
  discount: string;
  discountType: '%' | '$';
  categories: string[];
  vendors: string[];
  expiresOn: string;
  maxUsage: string;
}

interface EditDiscountFormProps {
  discountId: string;
  initialCode: string;
  initialDiscount: string;
  initialDiscountType: '%' | '$';
  initialCategories: string | string[];
  initialExpiresOn: string;
  initialMaxUsage: string;
  initialVendors: string[];
  categories: string[];
  vendors: Vendor[];
  onSubmit: (data: DiscountFormData) => Promise<void>;
  onCancel?: () => void;
}

const EditDiscountForm: React.FC<EditDiscountFormProps> = ({
  discountId,
  initialCode,
  initialDiscount,
  initialDiscountType,
  initialCategories,
  initialExpiresOn,
  initialMaxUsage,
  initialVendors,
  categories,
  vendors,
  onSubmit,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [discountTypeAnchor, setDiscountTypeAnchor] = useState<null | HTMLElement>(null);
  const [categoryAnchor, setCategoryAnchor] = useState<null | HTMLElement>(null);
  const [vendorsAnchor, setVendorsAnchor] = useState<null | HTMLElement>(null);

  const handleSubmit = async (data: DiscountFormData) => {
    try {
      setLoading(true);
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  // Convert initialCategories to array if it's a string
  const getInitialCategories = (): string[] => {
    if (Array.isArray(initialCategories)) {
      return initialCategories;
    }
    if (typeof initialCategories === 'string') {
      if (initialCategories === 'All categories' || initialCategories.trim() === '') {
        return [];
      }
      // Split comma-separated string
      return initialCategories.split(',').map(cat => cat.trim()).filter(cat => cat.length > 0);
    }
    return [];
  };

  const defaultValues: DiscountFormData = {
    code: initialCode,
    discount: initialDiscount,
    discountType: initialDiscountType,
    categories: getInitialCategories(),
    expiresOn: initialExpiresOn,
    maxUsage: initialMaxUsage,
    vendors: initialVendors,
  };

  return (
    <Box className="discount-form">
      <Form<DiscountFormData>
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        resetOnSubmit={false}
      >
        <DiscountFormFields
          categories={categories}
          vendors={vendors}
          discountTypeAnchor={discountTypeAnchor}
          setDiscountTypeAnchor={setDiscountTypeAnchor}
          categoryAnchor={categoryAnchor}
          setCategoryAnchor={setCategoryAnchor}
          vendorsAnchor={vendorsAnchor}
          setVendorsAnchor={setVendorsAnchor}
        />

        {/* Action Buttons */}
        <Box className="discount-form-actions">
          <FormButton
            type="submit"
            primary
            disabled={loading}
            size="full"
          >
            {loading ? 'Saving...' : 'Save changes'}
          </FormButton>
          {onCancel && (
            <FormButton
              type="button"
              primary={false}
              bordered={false}
              onClick={onCancel}
              size="full"
              disabled={loading}
            >
              Cancel
            </FormButton>
          )}
        </Box>
      </Form>
    </Box>
  );
};

// Component that uses form context for fields
const DiscountFormFields: React.FC<{
  categories: string[];
  vendors: Vendor[];
  discountTypeAnchor: HTMLElement | null;
  setDiscountTypeAnchor: (el: HTMLElement | null) => void;
  categoryAnchor: HTMLElement | null;
  setCategoryAnchor: (el: HTMLElement | null) => void;
  vendorsAnchor: HTMLElement | null;
  setVendorsAnchor: (el: HTMLElement | null) => void;
}> = ({
  categories,
  vendors,
  discountTypeAnchor,
  setDiscountTypeAnchor,
  categoryAnchor,
  setCategoryAnchor,
  vendorsAnchor,
  setVendorsAnchor,
}) => {
  const { watch, setValue, control } = useFormContext<DiscountFormData>();
  const watchedValues = watch();

  const updateField = <K extends keyof DiscountFormData>(
    field: K,
    value: DiscountFormData[K]
  ) => {
    setValue(field as any, value as any);
  };

  // Helper functions for date conversion
  const stringToDayjs = (dateString: string): Dayjs | null => {
    if (!dateString) return null;
    return dayjs(dateString).isValid() ? dayjs(dateString) : null;
  };

  const dayjsToString = (date: Dayjs | null): string => {
    if (!date || !date.isValid()) return '';
    return date.format('YYYY-MM-DD');
  };

  const today = dayjs().startOf('day');

  // Get display text for selects
  const getDiscountTypeText = () => watchedValues.discountType || '%';
  const getCategoryText = () => {
    const selectedCategories = watchedValues.categories || [];
    if (selectedCategories.length === 0) return 'All categories';
    if (selectedCategories.length === 1) {
      return selectedCategories[0];
    }
    return `${selectedCategories.length} categories selected`;
  };
  const getVendorsText = () => {
    const selectedVendors = watchedValues.vendors || [];
    if (selectedVendors.length === 0) return 'Select vendors';
    if (selectedVendors.length === 1) {
      const vendor = vendors.find(v => v.vendorId === selectedVendors[0]);
      return vendor?.companyName || vendor?.contactName || selectedVendors[0];
    }
    return `${selectedVendors.length} vendors selected`;
  };

  return (
    <>
      {/* Promocode Field */}
      <Box className="discount-form-input-wrapper">
        <FormInput
          name="code"
          label="Promocode"
          placeholder="Promocode"
          fullWidth
          className="discount-form-input"
        />
      </Box>

      {/* Discount Fields Row */}
      <Box className="discount-form-row">
        <Box className="discount-form-row-field discount-form-input-wrapper">
          <FormInput
            name="discount"
            label="Discount"
            placeholder="Discount"
            fullWidth
            className="discount-form-input"
          />
        </Box>
        <Box>
          <Button
            primary={false}
            onClick={(e) => setDiscountTypeAnchor(e.currentTarget)}
            className="discount-form-select-button"
          >
            {getDiscountTypeText()}
            <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
          </Button>
          <Dropdown
            anchorEl={discountTypeAnchor}
            open={Boolean(discountTypeAnchor)}
            onClose={() => setDiscountTypeAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            options={[
              { label: '%', value: '%', onClick: () => { updateField('discountType', '%'); setDiscountTypeAnchor(null); } },
              { label: '$', value: '$', onClick: () => { updateField('discountType', '$'); setDiscountTypeAnchor(null); } },
            ]}
          />
        </Box>
      </Box>

      {/* Applies to Field */}
      <Box className="discount-form-input-wrapper">
        <Typography className="discount-form-label">Applies to</Typography>
        <Box className="discount-form-category-wrapper">
          <Button
            primary={false}
            onClick={(e) => setCategoryAnchor(e.currentTarget)}
            className="discount-form-select-button"
          >
            {getCategoryText()}
            <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
          </Button>
        <Dropdown
          anchorEl={categoryAnchor}
          open={Boolean(categoryAnchor)}
          onClose={() => setCategoryAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          options={[
            {
              label: (watchedValues.categories || []).length === 0 ? '✓ All categories' : 'All categories',
              value: 'all',
              onClick: () => {
                updateField('categories', []);
                setCategoryAnchor(null);
              },
            },
            ...categories.map((category) => {
              const isSelected = (watchedValues.categories || []).includes(category);
              return {
                label: isSelected ? `✓ ${category}` : category,
                value: category,
                onClick: () => {
                  const currentCategories = watchedValues.categories || [];
                  const newCategories = isSelected
                    ? currentCategories.filter(cat => cat !== category)
                    : [...currentCategories, category];
                  updateField('categories', newCategories);
                },
              };
            }),
          ]}
        />
          {/* Category Tags */}
          {(watchedValues.categories || []).length > 0 && (
            <Box className="discount-form-category-tags">
              {(watchedValues.categories || []).map((category, index) => (
                <Box key={index} className="discount-form-category-tag">
                  <Typography>{category}</Typography>
                  <IconButton
                    onClick={() => {
                      const currentCategories = watchedValues.categories || [];
                      const newCategories = currentCategories.filter((_, i) => i !== index);
                      updateField('categories', newCategories);
                    }}
                    size="small"
                    sx={{ padding: 0 }}
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

    {/* Vendors Field */}
      <Button
        primary={false}
        onClick={(e) => setVendorsAnchor(e.currentTarget)}
        className="discount-form-select-button"
      >
        <span className={(watchedValues.vendors || []).length === 0 ? 'discount-form-placeholder' : ''}>
          {getVendorsText()}
        </span>
        <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
      </Button>
      <Dropdown
        anchorEl={vendorsAnchor}
        open={Boolean(vendorsAnchor)}
        onClose={() => setVendorsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        options={
          vendors.length === 0
            ? [{ label: 'No vendors available', value: 'empty', disabled: true, onClick: () => {} }]
            : vendors.map((vendor) => {
                const vendorName = vendor.companyName || vendor.contactName;
                const isSelected = (watchedValues.vendors || []).includes(vendor.vendorId);
                return {
                  label: isSelected ? `✓ ${vendorName}` : vendorName,
                  value: vendor.vendorId,
                  onClick: () => {
                    const currentVendors = watchedValues.vendors || [];
                    const newVendors = isSelected
                      ? currentVendors.filter(id => id !== vendor.vendorId)
                      : [...currentVendors, vendor.vendorId];
                    updateField('vendors', newVendors);
                  },
                };
              })
        }
      />

      {/* Expires on Field */}
      <Box className="discount-form-date-wrapper">
        <Controller
          name="expiresOn"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={stringToDayjs(field.value)}
              onChange={(newValue) => {
                const dateString = dayjsToString(newValue);
                updateField('expiresOn', dateString);
              }}
              minDate={today}
              slotProps={{
                textField: {
                  placeholder: 'Expires on',
                  fullWidth: true,
                  className: 'discount-form-input',
                  InputProps: {
                    endAdornment: <CalendarTodayIcon className="discount-form-date-icon" />,
                  },
                },
              }}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                },
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiOutlinedInput-root input': {
                  padding: '12px',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  '&::placeholder': {
                    color: 'var(--palette-light-text-secondary-value-hex)',
                    opacity: 1,
                  },
                },
              }}
            />
          )}
        />
      </Box>

      {/* Max Usage Field */}
      <Box className="discount-form-input-wrapper">
        <FormInput
          name="maxUsage"
          label="Max usage"
          placeholder="Max usage"
          fullWidth
          type="number"
          className="discount-form-input"
        />
      </Box>
    </>
  );
};

export default EditDiscountForm;
